import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { LobbyChallenge } from '@ccc/shared-types';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { useAuthStore } from '../lib/auth';
import { apiJson } from '../lib/multiplayer';

export function InviteRoute() {
  const user = useAuthStore((s) => s.user);
  const slug = window.location.pathname.split('/').filter(Boolean).at(-1) ?? '';
  const [challenge, setChallenge] = useState<LobbyChallenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    apiJson<{ challenge: LobbyChallenge }>(`/api/invite/${encodeURIComponent(slug)}`)
      .then((data) => {
        if (alive) setChallenge(data.challenge);
      })
      .catch((err) => {
        if (alive) setError(err instanceof Error ? err.message : 'That invite is not open.');
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      const data = await apiJson<{ gameUrl: string }>(`/api/invite/${encodeURIComponent(slug)}/accept`, {
        method: 'POST',
        body: '{}',
      });
      window.location.assign(data.gameUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept this invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full w-full max-w-[760px] mx-auto px-5 md:px-10 py-6 grid place-items-center">
      <Panel className="w-full p-6 text-center">
        <h1 className="font-display text-3xl font-bold">Private challenge</h1>
        {challenge ? (
          <>
            <p className="mt-2 text-sm text-ink-muted">
              {challenge.creatorName} invited you to a {challenge.rated ? 'rated' : 'casual'}{' '}
              {Math.floor(challenge.initialSeconds / 60)} + {challenge.incrementSeconds} game.
            </p>
            {user ? (
              <Button className="mt-5" onClick={accept} disabled={busy}>
                Accept invite
              </Button>
            ) : (
              <div className="mt-5 flex justify-center gap-3">
                <Link to="/login">
                  <Button>Log in</Button>
                </Link>
                <Link to="/join">
                  <Button variant="secondary">Join</Button>
                </Link>
              </div>
            )}
          </>
        ) : (
          <p className="mt-2 text-sm text-ink-muted">{error ?? 'Checking the invite...'}</p>
        )}
        {error && challenge && <p className="mt-3 text-sm text-danger">{error}</p>}
      </Panel>
    </div>
  );
}
